import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Empty, Row } from 'antd';
import { DoctorReviewRow, fetchDoctorReviews } from 'services/api/reviews';

import useOrgEffect from 'hooks/useOrgEffect';
import { PageWrapper } from 'components/ui';
import { Review } from './components';

const PAGE_LIMIT = 10;

const DoctorReviews: FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [isPaging, setPaging] = useState(false);
  const [results, setResults] = useState<DoctorReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useOrgEffect(() => {
    let cancelled = false;
    const isInitial = page === 1;

    const load = async () => {
      try {
        if (isInitial) setLoading(true);
        else setPaging(true);
        const res = await fetchDoctorReviews({
          page,
          limit: PAGE_LIMIT
        });

        if (cancelled) return;
        const rows = res.data?.data ?? [];
        setTotal(res.data?.total ?? 0);
        setResults(prev => (isInitial ? rows : [...prev, ...rows]));
      } catch {
        // swallow — empty/partial list on failure
      } finally {
        if (!cancelled) {
          if (isInitial) setLoading(false);
          else setPaging(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page]);

  return (
    <PageWrapper
      title={t('doctor_reviews.page_title')}
      color="white"
      loading={loading}
    >
      {results.length ? (
        <>
          {results.map(item => (
            <Review
              key={item.id}
              rate={Number(item.rating)}
              content={item.review || ''}
              date={item.created_at}
              sender={
                item.sender
                  ? {
                      firstName: item.sender.first_name || '',
                      lastName: item.sender.last_name || '',
                      email: item.sender.email ?? ''
                    }
                  : null
              }
              receiver={
                item.receiver
                  ? {
                      firstName: item.receiver.first_name || '',
                      lastName: item.receiver.last_name || '',
                      email: item.receiver.email ?? ''
                    }
                  : null
              }
            />
          ))}
          {total > results.length && (
            <Row justify="center">
              <Button
                type="default"
                onClick={() => setPage(prev => prev + 1)}
                loading={isPaging}
              >
                {t('doctor_reviews.load_more')}
              </Button>
            </Row>
          )}
        </>
      ) : (
        <Empty />
      )}
    </PageWrapper>
  );
};

export default DoctorReviews;
